import cn from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ClrIconBtn } from 'vrlcm-gui-components';
import { Link } from 'react-router-dom';
import { ShellApp, User, VisitedPage } from 'vrlcm-gui-types';
import {
  recentVisitedPageByUserUtil,
  shellAppToDisplayName,
  UserContext
} from 'vrlcm-gui-utils';
import './RecentVisitedPages.css';
import { dateFormatters, formatDate, l10n } from 'i18n/i18nUtil';

const RecentVisitedPageItem: React.FC<{
  app: {
    displayName: string;
    logo: string;
  };
  page: VisitedPage;
  onDelete: () => void;
}> = ({ page, onDelete, app }) => {
  const [deleted, setDeleted] = useState(false);

  const onDeleteHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    setDeleted(true);
    setTimeout(() => onDelete(), 300);
  };

  return (
    <div
      className={cn('recent-visited-page-item-wrapper', {
        'recent-visited-page-item-wrapper--deleted': deleted
      })}>
      <Link
        className="recent-visited-page-item__anchor"
        to={page.urlWithoutHost}>
        <div className="recent-visited-page-item">
          <div className="recent-visited-page-item__details">
            <div className="recent-visited-page-item__app">
              {app.logo && (
                <img
                  className="recent-visited-page-item__app__img"
                  src={app.logo}
                  alt={app.displayName}
                />
              )}
              <span className="recent-visited-page-item__app__name">
                {app.displayName}
              </span>
            </div>
            <span
              className="recent-visited-page-item__url"
              title={page.urlWithoutHost}>
              {page.urlWithoutHost}
            </span>
          </div>
          <div className="recent-visited-page-item__date">
            {formatDate(page.when, dateFormatters.SHORT)}
          </div>
        </div>
      </Link>
      <div className="recent-visited-page-item__actions">
        <ClrIconBtn
          shape="trash"
          onClick={onDeleteHandler}
          title="Delete"
          fill="#C92100"
        />
      </div>
    </div>
  );
};

const RecentVisitedPagesController: React.FC<{
  user: User;
  apps: ShellApp[];
}> = ({ user, apps }) => {
  const [recentVisitedPages, setRecentVisitedPages] = useState<VisitedPage[]>(
    []
  );

  const {
    onChange,
    getRecentVisitedPages,
    removeVisitedPage
  } = recentVisitedPageByUserUtil;

  onChange(newRecentVisitedPages =>
    setRecentVisitedPages(newRecentVisitedPages)
  );

  useEffect(() => setRecentVisitedPages(getRecentVisitedPages(user.name)), [
    getRecentVisitedPages,
    user.name
  ]);

  const onDeleteVisitedPageHandler = useCallback(
    (page: VisitedPage) => () =>
      setRecentVisitedPages(removeVisitedPage(user.name, page)),
    [removeVisitedPage, user.name]
  );

  return (
    <div className="recent-visited-pages">
      <h4>{l10n('components.withAppRoot.recentlyVisitedPages')}</h4>
      <div className="recent-visited-pages__items">
        {recentVisitedPages
          .filter(p => apps.some(a => a.vmid === p.app))
          .map(p => ({
            page: p,
            app: {
              displayName: shellAppToDisplayName(
                apps.find(a => a.vmid === p.app) as ShellApp
              ),
              logo: `/assets/${
                (apps.find(a => a.vmid === p.app) as ShellApp).icon
              }`
            }
          }))
          .map(({ page, app }) => (
            <RecentVisitedPageItem
              page={page}
              key={page.urlWithoutHost}
              app={app}
              onDelete={onDeleteVisitedPageHandler(page)}
            />
          ))}
        {recentVisitedPages.length === 0 && (
          <span>{l10n('components.withAppRoot.noRecentlyVisitedPages')}</span>
        )}
      </div>
    </div>
  );
};

const RecentVisitedPages: React.FC<{ apps: ShellApp[] }> = ({ apps }) => {
  const user = useContext(UserContext);
  return user ? <RecentVisitedPagesController user={user} apps={apps} /> : null;
};

export default RecentVisitedPages;
